/**
 * EPIC 40 TND — types du module « Appels d'offres ».
 *
 * ⚠️ Le vocabulaire fermé (buckets, états de pièce, niveaux de signature,
 * `kind` de statut) reprend EXACTEMENT les CHECK de la base. Le laisser ouvert
 * ferait passer une valeur inventée jusqu'à un 500 citant un nom de contrainte,
 * là où l'écran doit simplement ne pas la proposer.
 */

import type { ClauseEtendue, IndiceDeRevision } from '@/lib/revisionClause'
import { phraseDEcranDuRefus } from './useTenders.refus'

export const TENDERS_ENDPOINT = '/api/sales'

/** `kind` d'un statut de pipeline — c'est lui qui décide du droit exigé. */
export type TenderStatusKind = 'OPEN' | 'WON' | 'LOST' | 'CLOSED'

export interface TenderStatus {
    id: string
    label: string
    color?: string | null
    kind: TenderStatusKind
    stage_code?: string | null
    parent_stage_code?: string | null
    position: number
    is_active: boolean
}

export interface TenderOutcomeReason {
    id: string
    /** `null` sur tout motif créé par un organisme. Ni créable ni modifiable. */
    code: string | null
    label: string
    /**
     * 🔴 **Les kinds auxquels le motif s'applique — PAS un `kind` unique.**
     *
     * Ce champ s'appelait `kind: TenderStatusKind` côté front. Le serveur n'a
     * jamais rendu ça : `TenderOutcomeReasonResponse.applicable_kinds` est une
     * LISTE, et beaucoup de motifs en portent deux (« Hors délai » vaut pour une
     * perte comme pour un abandon). `reason.kind` valait donc `undefined` à
     * l'exécution — invisible, parce que le hook n'avait aucun consommateur.
     */
    applicable_kinds: string[]
    position: number
    is_native: boolean
    is_active: boolean
    /**
     * Nombre de fiches portant ce motif. Présent seulement si `with_counts`.
     * Il distingue « supprimable » de « seulement retirable de la liste » :
     * supprimer un motif déjà porté trouerait les statistiques qui le citent.
     */
    tenders_count: number | null
}

/**
 * Les 6 procédures de passation (TND-001) — référentiel PUBLIC.
 *
 * ⚠️ La route rend `{ procedures: [...] }`, PAS un tableau nu (le type disait l'inverse :
 * `procedures.map is not a function` au premier rendu). Chaque ligne porte `label` (le
 * français, jamais `label_fr`) et `label_en` ; l'écran choisit par `useProceduresDansLaLangue`.
 * Un type front qui décrit une réponse que le serveur n'envoie pas ne protège de rien.
 */
export interface ProcedureType {
    code: string
    label: string
    label_en?: string | null // absent ou vide → `label`
    is_formalized: boolean
    allows_negotiation: boolean
}

/** L'enveloppe de `GET /api/sales/tenders/referentials`. */
export interface ProcedureTypesPayload {
    procedures: ProcedureType[]
}

export interface TenderListItem {
    id: string
    auto_reference: string
    title: string
    buyer_name: string | null
    buyer_reference: string | null
    client_id: string | null
    client_name: string | null
    status_id: string
    status_label: string | null
    status_color: string | null
    status_kind: TenderStatusKind | null
    stage_code: string | null
    procedure_code: string | null
    procedure_label: string | null
    estimated_amount: string | null
    currency: string | null
    offers_deadline_at: string | null
    /**
     * La prochaine échéance NON ACQUITTÉE — retards compris, toutes natures,
     * quelle que soit l'étape.
     *
     * ⚠️ À ne pas confondre avec `offers_deadline_at`, qui est la seule date
     * de REMISE. Les deux coïncident souvent avant le dépôt et divergent
     * toujours après — et c'est justement après que le suivi se perd.
     */
    next_deadline_at: string | null
    published_at: string | null
    status_changed_at: string | null
    execution_city: string | null
    cpv_codes: string[] | null
    owner_id: string | null
    owner_name: string | null
    /** TND-020 — les deux autres responsables, filtrables comme le chargé de réponse.
     *  Le contact administratif est LU sur le client : sans client, il est `null`. */
    market_manager_id: string | null
    market_manager_name: string | null
    client_admin_owner_id: string | null
    client_admin_owner_name: string | null
    entity_id: string | null
    /** L'organisme PORTEUR — nécessaire dès qu'un siège regarde ses agences. */
    entity_name: string | null
    /**
     * La décision Go / No-Go, DANS le listing.
     *
     * 🔑 Le serveur la projette, la trie, la filtre, la groupe et la compte
     * depuis la livraison — et aucune colonne ne l'exposait. Un champ qu'aucune
     * liste ne montre et qu'aucun filtre n'atteint est un champ écrit que rien
     * ne relit : le bilan « pourquoi écartons-nous 60 % des consultations » se
     * fait sur cette colonne, pas fiche par fiche.
     */
    bid_decision: 'GO' | 'NO_GO' | null
    lots_count: number | null
    created_source: string | null
    /** Lien vers l'AVIS publié — distinct de `buyer_profile_url` (le profil
     *  acheteur, qui liste TOUTES ses consultations). */
    notice_url: string | null
    /**
     * 36_CUSTOM §8 — les champs personnalisés du locataire, tels que le serveur
     * les rend : un objet `{ code: valeur }` DÉJÀ décodé côté dépôt.
     *
     * 🔑 `null` et `{}` ne disent pas la même chose, et toute la détectabilité
     * tient à cet écart : `null` = « le serveur ne me l'a pas dit » (une
     * projection a oublié la colonne), `{}` = « cette fiche n'a aucun champ
     * personnalisé », ce qui est une réponse vraie.
     *
     * ⚠️ Déclaré ICI, sur la ligne du listing, et pas sur `TenderDetail` : la
     * fiche l'hérite par `Omit<TenderListItem, …>`, donc une seule déclaration
     * sert les deux — et le gardien serveur
     * (`test_aucun_champ_du_serveur_ne_manque_a_lecran`) lit les deux sens.
     */
    custom_data: Record<string, unknown> | null
    created_at: string
    updated_at: string
    editable?: boolean
    deletable?: boolean
    is_inherited?: boolean
}

/**
 * 🔴 **La FICHE et le LISTING ne rendent PAS la même forme — et ce type le
 * niait.**
 *
 * `TenderDetail extends TenderListItem` faisait hériter les champs PLATS du
 * listing (`status_label`, `status_kind`, `stage_code`, `owner_name`,
 * `client_name`, `procedure_label`). Or `GET /tenders/{id}` rend des objets
 * IMBRIQUÉS : `status: {id, stage_code, label, kind, color}`,
 * `owner: {id, name}`, `client`, `procedure`.
 *
 * Résultat : toute la synthèse de la fiche affichait « — ». Client, procédure,
 * chargé de réponse, fin d'exécution, origine : six champs vides sur une fiche
 * dont la base contenait tout. Et la pastille de statut sortait sans libellé.
 *
 * TypeScript ne pouvait rien dire : un type d'interface est une DÉCLARATION
 * posée sur du JSON, pas une vérification. C'est `unContratQuiSeVerifie` qui
 * confronte désormais ce type au modèle Pydantic du serveur.
 */
export interface TenderDetail
    extends Omit<
        TenderListItem,
        | 'status_id'
        | 'status_label'
        | 'status_color'
        | 'status_kind'
        | 'stage_code'
        | 'owner_id'
        | 'owner_name'
        | 'market_manager_id'
        | 'market_manager_name'
        | 'client_admin_owner_id'
        | 'client_admin_owner_name'
        | 'client_id'
        | 'client_name'
        | 'procedure_code'
        | 'procedure_label'
        | 'entity_name'
        | 'lots_count'
        | 'notice_url'
    > {
    /** IMBRIQUÉ — jamais `status_label` / `status_kind` / `stage_code` à plat. */
    status: {
        id: string
        stage_code: string | null
        label: string
        kind: TenderStatusKind
        color: string
        is_native: boolean
    }
    /** ① Le CHARGÉ DE RÉPONSE — son travail s'arrête à la remise du pli. */
    owner: { id: string; name: string } | null
    /** ② Le RESPONSABLE DU MARCHÉ — questions acheteur, révision annuelle des
     *  prix, synthèses d'exécution, recandidature. Deux à quatre ans. */
    market_manager: { id: string; name: string } | null
    /** ③ Le CONTACT ADMINISTRATIF DU COMPTE — celui qui planifie les sessions.
     *
     *  🔴 Il est LU de la fiche client (`clients.admin_owner_id`), jamais
     *  recopié sur le marché : deux marchés du même client ne peuvent donc pas
     *  en porter deux. Le modifier depuis l'onglet Équipe écrit sur le CLIENT.
     *  `null` tant qu'aucun client n'est rattaché. */
    client_admin_owner: { id: string; name: string } | null
    /** Le responsable COMMERCIAL du compte, même provenance. Lecture seule ici :
     *  il pilote tout le cycle et se règle depuis la fiche client. */
    client_sales_owner: { id: string; name: string } | null
    client: { id: string; name: string; auto_reference?: string | null; locked_reason?: 'scope' | 'anonymised' | null } | null
    procedure: {
        code: string
        label: string
        is_formalized?: boolean
        allows_negotiation?: boolean
    } | null
    description: string | null
    /** Sur la FICHE seulement : la projection du listing ne la porte pas. */
    execution_end_date: string | null
    /**
     * 🔴 Ces onze champs étaient RENVOYÉS par le serveur et ABSENTS de ce type.
     *
     * Le contrat `TenderResponse` les porte depuis toujours ; l'écran ne pouvait
     * ni les lire ni les renvoyer, si bien que l'édition de la fiche était
     * impossible sur tout ce qui touche à l'acheteur, à la publication et au
     * lieu d'exécution. Un type incomplet ne lève rien : il rend simplement une
     * partie du contrat invisible.
     */
    buyer_siret: string | null
    buyer_profile_url: string | null
    publication_channel: string[]
    submission_platform: string | null
    execution_address_street: string | null
    execution_address_zip: string | null
    execution_address_city: string | null
    execution_address_country: string
    execution_latitude: string | null
    execution_longitude: string | null
    execution_start_date: string | null
    /** L'appel d'offres DONT celui-ci est la recandidature, s'il y en a un. */
    parent_tender_id: string | null
    /** L'instant où l'affaire gagnée est devenue client et contrats cadres. */
    transformed_at: string | null
    /** Post-mortem : renseignés quand l'acheteur publie son rapport d'analyse. */
    our_score: string | null
    winner_score: string | null
    winner_name: string | null
    questions_deadline_at: string | null
    max_amount: string | null
    /**
     * Le motif d'issue — l'IDENTIFIANT, pas le libellé : le serveur ne résout
     * pas les référentiels dans la fiche. L'écran le résout depuis
     * `useTenderOutcomeReasons`, déjà en cache pour la modale de transition.
     */
    outcome_reason_id: string | null
    outcome_notes: string | null
    /**
     * TND-003 — la décision de RÉPONDRE, distincte de l'étape franchie.
     *
     * `null` tant que personne n'a tranché — et elle ne se DÉDUIT jamais du rang
     * atteint : on traverse « Go / No-Go » par un glisser-déposer, par un saut
     * ou par un import de la veille. Un refus n'a pas de motif propre : il EST
     * une clôture, et réutilise `outcome_reason_id` ci-dessus.
     */
    bid_decision: 'GO' | 'NO_GO' | null
    bid_decision_at: string | null
    bid_decision_by: string | null
    /** Le NOM, résolu par le serveur — `null` si le compte a été supprimé. */
    bid_decision_by_name: string | null
    /** TND-017 — lien vers l'AVIS publié (≠ `buyer_profile_url`). */
    notice_url: string | null
    source_notice_id: string | null
    /** L'avis normalisé, en LECTURE SEULE : aucune route d'écriture ne l'expose. */
    source_payload: Record<string, unknown> | null
    source_synced_at: string | null
    notes: string | null
    anonymized_at: string | null
    /** La référence VERSÉE depuis cette fiche (`POST /to-reference`) : « déjà versé »
     *  se dit avant le clic. Absente d'une réponse d'avant ce champ, encore en cache. */
    market_reference_id?: string | null
    /** TAGS-001 — la ligne d'étiquettes de l'en-tête, sur la FICHE seulement. */
    tags: { id: string; name: string; color: string | null }[]
    lots?: TenderLot[]
}

export interface TenderLot {
    id: string
    tender_id: string
    /** VARCHAR(20) en base, jamais un entier : un acheteur publie « 1 », mais
     *  aussi « 2A » ou « 03 ». Le typer en nombre ferait afficher « NaN » sur
     *  une consultation parfaitement ordinaire. */
    lot_number: string
    title: string
    estimated_amount: string | null
    currency: string | null
    status_id: string | null
    transformed_at: string | null
    transformed_contract_id: string | null
}

// ── TND-006 — échéances ─────────────────────────────────────────────────────

/** Les 11 types d'échéance (`rc_other_date` : arbitrage A5). `is_hard` : ÉLIMINATOIRE ou non. */
export type TenderDeadlineKind =
    | 'questions_limit'
    | 'offers_limit'
    | 'attributaire_proofs_due'
    | 'buyer_request_due'
    | 'award_forecast'
    | 'execution_start'
    | 'standstill_end'
    | 'motifs_due'
    | 'contractual_ref_deadline'
    | 'rebid_alert'
    | 'rc_other_date'

export interface TenderDeadline {
    id: string
    tender_id: string
    kind: TenderDeadlineKind
    due_at: string
    is_hard: boolean
    source: 'rc' | 'avis' | 'q_reponse' | 'manuel' | 'ia' | 'calcul'
    /** Libellé libre, quand l'échéance ne se résume pas à son `kind`. */
    label: string | null
    /** Trace du calcul réglementaire — `null` si la date a été SAISIE ; elle rend la date prouvable. */
    computed_from: Record<string, unknown> | null
    /** Acquittement : il STOPPE les rappels restants. Les champs s'appelaient
     *  `acknowledged_at` / `task_notification_id` dans ce type — deux noms que
     *  le serveur n'envoie jamais : l'écran aurait lu `undefined` et affiché
     *  une échéance non acquittée sur une échéance close. */
    done_at: string | null
    done_by: string | null
    /**
     * Combien de tâches PORTE cette échéance — pas « laquelle ».
     *
     * 🔴 Ce champ s'appelait `linked_task_id` : un pointeur UNIQUE, supprimé par
     * la migration 676 au profit d'une table de liaison. Une échéance en porte
     * désormais autant que le métier en demande (« une ou deux », PO) : demander
     * des motifs et préparer le référé sont deux travaux, pas un.
     *
     * `task_count` est compté par JOINTURE sur `notifications` : une tâche
     * supprimée cesse d'être comptée, si bien que le rail ne peut pas annoncer
     * une tâche qui n'existe plus. Le serveur le rend TOUJOURS, jamais nul —
     * une échéance sans tâche sort à 0, elle ne disparaît pas de l'agrégat.
     */
    task_count: number
    /**
     * Sous-ensemble de `task_count` dont le statut n'est ni `TREATED`, ni
     * `AUTO_RESOLVED`, ni `IGNORED`. Invariant : `0 ≤ open_task_count ≤ task_count`.
     *
     * ⚠️ Les deux comptes disent TROIS états, pas deux : « 2 tâches, toutes
     * faites » n'est pas « aucune tâche ». Confondre `open_task_count === 0`
     * avec l'absence de tâche ferait reproposer un travail déjà mené.
     */
    open_task_count: number
    created_at: string
    updated_at: string
}

// ── TND-007 — checklist des pièces ──────────────────────────────────────────

export type PieceBucket = 'candidature' | 'offre' | 'attributaire'
export type PieceState = 'todo' | 'draft' | 'ready' | 'signed' | 'deposited'
export type SignatureLevel = 'none' | 'advanced_qualified' | 'qualified'
export type SignatureFormat = 'xades' | 'cades' | 'pades'

export interface TenderPieceFile {
    id: string
    name: string | null
    size_bytes: number | null
    mime_type: string | null
    /**
     * « Ce fichier est un brouillon produit par Argalis » (marqueur du générateur DC, lu par
     * le serveur) : il rend le SECOND CLIC atteignable — `verdictDeGeneration` offre « Le
     * regénérer » sur notre brouillon et garde « détachez d'abord » sur celui d'un humain.
     *
     * ⚠️ REQUIS comme côté serveur (`PieceFile`), `retained` aussi : `tsc` et la couture le disent.
     */
    generated_by_argalis: boolean
    /** Sous conservation légale (marché gagné) : pas de « Détacher » d'une fiche anonymisée. */
    retained: boolean
}

export interface TenderPiece {
    id: string
    tender_id: string
    lot_id: string | null
    lot_number: string | null
    lot_title: string | null
    bucket: PieceBucket
    code: string
    label: string | null
    required: boolean
    state: PieceState
    assignee_id: string | null
    assignee_name: string | null
    due_at: string | null
    issued_at: string | null
    signature_level: SignatureLevel
    signature_format: SignatureFormat | null
    requested_by_buyer: boolean
    is_seeded: boolean
    notes: string | null
    task_notification_id: string | null
    created_at: string
    updated_at: string
    file: TenderPieceFile | null
    /**
     * La pièce POINTE un fichier que la GED n'a plus (suppression douce).
     * L'écran doit ALERTER : sans ce drapeau, la ligne se lirait « déposée »
     * sur un dossier qui a perdu sa pièce.
     */
    file_missing: boolean
    /**
     * TND-008 §8.5 bis (décision PO du 2026-09-20) — l'instant où la RÉTENTION
     * a coupé le lien, cinq ans après la clôture de la consultation. Non `null`
     * = un document AVAIT été fourni : la ligne garde la trace, elle perd le
     * fichier.
     *
     * ⚠️ À ne pas confondre avec `file_missing`, qui dénonce une ANOMALIE
     * (« rejoignez-en un avant le dépôt »). Ici la fin de vie est normale, et
     * l'afficher en alerte ferait crier au défaut sur un dossier en règle.
     * Sans ce champ la ligne retomberait sur « Aucun document rattaché » — une
     * checklist qui, au bout de cinq ans, dirait que rien n'a jamais été remis.
     */
    file_released_at: string | null
    ready: boolean
    lot_splittable: boolean
    /**
     * Le régime de fraîcheur du code — c'est lui qui dit si la ligne porte une
     * date d'émission, et laquelle des deux règles s'y applique.
     *
     * ⚠️ Il vient du SERVEUR : recopier ici les codes concernés en ferait une
     * troisième source de vérité, muette le jour où l'on en ajoute un.
     */
    freshness_regime: 'social' | 'fiscal' | null
    /**
     * CODE d'avertissement, jamais une phrase — l'écran le résout dans la
     * langue de qui regarde.
     *
     * 🔴 Le serveur rendait ici une phrase française (« attestation de plus de
     * six mois… »), affichée telle quelle : la ligne ambre restait française en
     * interface anglaise.
     */
    freshness_warning_code: string | null
}

/**
 * Une pièce requise encore manquante, telle que la GARDE la rend.
 *
 * ⚠️ Ce n'est PAS une `TenderPiece` : la garde ne projette que six colonnes. La déclarer
 * comme une pièce complète faisait valoir `undefined` à `id` — donc des clés React
 * dupliquées sur toutes les puces — et à `lot_number`, si bien que la mention « (lot X) »
 * ne s'affichait jamais : deux pièces homonymes sur deux lots différents apparaissaient en
 * doublon identique, sans moyen de savoir lequel des deux lots était incomplet.
 */
export interface MissingPiece {
    id: string
    code: string
    label: string | null
    bucket: PieceBucket
    lot_id: string | null
    lot_number: string | null
}

export interface TenderChecklist {
    pieces: TenderPiece[]
    /** Garde BLOQUANTE de « Déposé » ; celle de l'`attributaire` n'est qu'INFORMATIVE. */
    deposit: { ready: boolean; missing: MissingPiece[]; buckets: PieceBucket[] }
    attributaire: { complete: boolean; missing: MissingPiece[] }
    use_dume: boolean
    paquets_figes: PieceBucket[] // structure figée : ni corbeille ni « Exiger » (`content_lock.paquets_figes`)
    pieces_exclues_dume?: string[] // « Exiger » y serait refusé : le DUME exclut DC1/DC2 (serveur)
}

// ── TND-008 — dossier documentaire ──────────────────────────────────────────

export interface TenderFolder {
    id: string
    parent_id: string | null
    name: string
    path: string
    depth: number
    position: number
    is_system: boolean
    system_key: string | null
    documents_count: number
}

export interface TenderDocument {
    id: string
    original_name: string
    mime_type: string
    size_bytes: number
    folder_id: string | null
    status: string
    metadata: Record<string, unknown> | null
    created_at: string
    updated_at: string
    uploaded_by: string | null
    /**
     * Quand la pièce a été mise DE CÔTÉ — `null` si elle est active. 🔴 Servie depuis
     * le 2026-09-18 (`list_tender_documents`), et REQUISE à dessein.
     * La fiche AO est le seul écran qui liste les deux états : le coffre et les
     * onglets Documents des fiches filtrent `archived_at IS NULL`. La déclarer
     * facultative ferait de l'étiquette « Archivé » une case à cocher qu'un futur
     * consommateur oublie — et l'archivage redeviendrait silencieux.
     */
    archived_at: string | null
    has_legal_retention: boolean // marché gagné : 409 à la corbeille — `TenderDocumentDeleteAction`
}

export interface TenderDocumentsPayload {
    folders: TenderFolder[]
    documents: TenderDocument[]
    deposit_categories: string[]
    documents_has_more: boolean // C181-bis — la borne serveur a coupé `documents` : l'écran le DIT.
}

// ── TND-022 — sous-onglet de la fiche client ────────────────────────────────
// Sorti dans `useTenders.types.client.ts` (plafond de taille) ; ré-exporté ici.
export type {
    ClientTenderItem,
    ClientTendersPage,
    ClientTendersSummary,
} from './useTenders.types.client'

// ── TND-002 — lots ──────────────────────────────────────────────────────────

/** L'état de notre RÉPONSE sur ce lot — pas celui du lot chez l'acheteur. */
/**
 * 🔴 **Ce type décrivait SIX valeurs dont aucune n'existe.**
 *
 * Il déclarait `PLANNED | BIDDING | SUBMITTED | WON | LOST | ABANDONED` ; la
 * base, le `CHECK ck_tender_lots_bid_status` et l'énumération Python disent
 * `A_DECIDER | REPOND | NE_REPOND_PAS | GAGNE | PERDU`. Les deux vocabulaires
 * n'ont pas une valeur en commun.
 *
 * Conséquence invisible : `TONE[lot.bid_status]` rendait `undefined` sur CHAQUE
 * lot, et la pastille était rendue sans aucune couleur — `className="shrink-0
 * undefined"`. Le libellé, lui, s'affichait correctement (les clés i18n, elles,
 * portaient les bons codes), si bien que l'écran avait l'air juste : seule la
 * couleur manquait, et rien ne dit qu'une couleur manque.
 *
 * `tsc` ne pouvait rien voir : le serveur rend une chaîne, et c'est ce type qui
 * DÉCLARE ce qu'elle vaut. Un type qui décrit autre chose que son émetteur se
 * vérifie tout seul.
 *
 * Le gardien `__tests__/positionsDeReponseDuMoteur.test.ts` dérive désormais ces
 * cinq valeurs du fichier Python.
 */
export type TenderBidStatus = 'A_DECIDER' | 'REPOND' | 'NE_REPOND_PAS' | 'GAGNE' | 'PERDU'

export interface TenderLotDetail {
    id: string
    tender_id: string
    /** VARCHAR côté base : un acheteur numérote « 1 », « 2A », « 3 bis ». */
    lot_number: string
    title: string
    estimated_amount: string | null
    currency: string
    cpv_code: string | null
    procedure_code: string | null
    bid_status: TenderBidStatus
    notes: string | null
    /**
     * Les DEUX autres montants d'un accord-cadre, au niveau du LOT.
     *
     * L'estimé n'engage personne. Le MINIMUM est ce que l'acheteur s'engage à
     * commander — le seul chiffre d'affaires certain, et le premier qu'on
     * regarde en décidant si l'on répond. Le MAXIMUM est le plafond de CE lot :
     * celui de la consultation vaut pour l'ensemble, ce qui est faux dès qu'elle
     * est allotie.
     */
    min_guaranteed_amount: string | null
    max_amount: string | null
    /** La volumétrie du CCTP, en prose. Elle ne calcule rien. */
    expected_volume_note: string | null
    /**
     * POURQUOI ce lot est perdu ou écarté — MÊME référentiel que pour un marché
     * entier (`tender_outcome_reasons`).
     *
     * 🔑 C'est ce champ que compte le grain « par lot » du tableau de bord.
     * Sans lui, retourner une tuile afficherait deux ventilations vides sur un
     * portefeuille pourtant décidé — le pire des affichages, celui qui donne à
     * croire qu'il n'y a rien à apprendre.
     *
     * ⚠️ Le serveur l'EXIGE dès que `bid_status` passe à `PERDU` ou
     * `NE_REPOND_PAS`, et l'EFFACE quand le lot repasse à une position ouverte :
     * un motif décrit une issue, il ne survit pas à sa disparition.
     */
    outcome_reason_id: string | null
    outcome_notes: string | null
    created_at: string
    updated_at: string
}

// ── TND-010 — bordereau de prix ─────────────────────────────────────────────

export interface TenderPriceLine {
    id: string
    tender_id: string
    lot_id: string
    position: number
    label: string
    item_type: string | null
    item: { id: string; label: string; reference?: string | null } | null
    /**
     * Tarif public de l'élément lié — INFORMATIF : il sert à voir l'écart,
     * jamais à le refuser.
     *
     * ⚠️ Le constructeur de réponse ne le pose JAMAIS et le SQL ne le
     * sélectionne pas : il vaut `null` sur 100 % des lignes, y compris celles
     * rattachées à un article du catalogue. Tout affichage conditionné à sa
     * présence est donc du code mort. Le champ reste déclaré parce que le
     * schéma serveur le déclare — mais l'écran ne doit rien construire dessus
     * tant que la jointure catalogue n'est pas faite (dette TND-010).
     */
    catalog_reference_price: string | null
    pricing_base_unit: string | null
    unit_price: string
    currency: string
    dqe_quantity: string | null
    /** `unit_price × dqe_quantity`, calculé SERVEUR pour que l'écran et l'export
     *  ne puissent pas arrondir différemment. */
    dqe_line_total: string | null
    price_cap: string | null
    notes: string | null
}

/** Les trois indices de révision — `RevisionIndex` côté serveur, `CHECK` en base
 *  (migrations 608 et 620). Le type se ferme parce que l'écran CONSTRUIT sa clé
 *  i18n à partir de cette valeur : un `string` ouvert laissait passer un indice
 *  neuf, qui se serait affiché en brut (« Prix révisables (INSEE_BT01) ») sans
 *  qu'aucun extracteur ne le voie. */
export type TenderRevisionIndex = IndiceDeRevision

/** Les champs de la migration 920 (`ClauseEtendue`) arrivent avec la grille. */
export interface TenderLotGrid extends ClauseEtendue {
    lot: { id: string; lot_number: string; title: string }
    revision_index: TenderRevisionIndex
    revision_fixed_part: string | null
    revision_base_index: string | null
    revision_base_index_date: string | null
    /** TND-013 — ce qui manque à la clause, jugé par le VERDICT serveur
     *  (`champs_manquants_de_la_clause`). La pastille le LIT, ne rejuge rien. */
    revision_missing_fields: string[]
    lines: TenderPriceLine[]
    dqe_total: string | null
    /** Le total est suffixé dès que ce compte n'est pas nul : un total qui a
     *  l'air complet sans l'être est un mensonge. */
    lines_without_qty: number
    free_lines_count: number
    estimated_coverage_pct: string | null
}

export interface TenderPriceGrid {
    tender_id: string
    currency: string
    /** TOUS les lots, y compris ceux dont la grille est vide : une grille
     *  absente est une information, et la masquer la ferait oublier. */
    lots: TenderLotGrid[]
    editable: boolean
    /** POURQUOI `editable` est faux, `null` s'il est vrai : c'est lui qui choisit la phrase. */
    locked_reason: 'won' | 'anonymised' | null
    /** Stage ≥ « Déposé » : l'écriture reste possible — préparer une
     *  négociation est légitime — mais l'écran avertit que le pli remis fait foi. */
    post_deposit: boolean
}

// ── TND-009 — échanges avec l'acheteur ──────────────────────────────────────

/**
 * L'enveloppe de `GET /tenders/{id}/questions`.
 *
 * ⚠️ Comme le référentiel des procédures, cette route rend un OBJET paginé,
 * pas un tableau nu. Les voisines (`/lots`, `/deadlines`) rendent, elles, des
 * tableaux : la forme se vérifie route par route, elle ne se devine pas au nom.
 */
export interface TenderQuestionsPayload {
    items: TenderQuestion[]
    next_cursor: string | null
    has_more: boolean
    /**
     * ⚠️ **Deux compteurs NOMMÉS, pas un dictionnaire libre.** « 2 échanges sans
     * réponse » ne dit pas quoi faire ; « 1 question en attente de l'acheteur ·
     * 1 demande de l'acheteur à traiter » distingue ce qu'on relance de ce qu'on
     * traite. Le déclarer `Record<string, number>` laissait passer n'importe
     * quelle clé et n'en garantissait aucune.
     *
     * `null` quand la page n'est pas la première : les compteurs portent sur
     * TOUT l'appel d'offres, pas sur la page.
     */
    counts: { pending_of: number; pending_buyer: number } | null
}

export interface TenderQuestion {
    id: string
    tender_id: string
    lot_id: string | null
    direction: string
    kind: string
    status: string
    /** ⚠️ Il n'existe AUCUN champ `subject` côté serveur : les deux seules
     *  zones de texte sont `question` et `answer`. Un `subject` déclaré ici
     *  aurait fait disparaître silencieusement la ligne de titre de l'échange. */
    question: string | null
    answer: string | null
    asked_at: string | null
    answered_at: string | null
    /**
     * L'acheteur a modifié le DCE : tout ce qui a été rédigé avant peut être
     * caduc.
     *
     * 🔴 Ce drapeau ne s'éteint JAMAIS — l'acquittement pose
     * `dce_impact_acked_at` À CÔTÉ, il ne retire pas le fait. C'est voulu : le
     * DCE a bien été modifié, et ça reste vrai. L'écran doit donc filtrer sur
     * `dce_impact_acked_at === null`, jamais sur `dce_impact` seul — sinon le
     * bouton « J'ai relu » ne change rien à l'affichage, et l'utilisateur
     * clique en boucle sur un bandeau qui ne part pas.
     */
    dce_impact: boolean
    dce_impact_acked_at: string | null
    /** Pièces jointes déjà RÉSOLUES par le serveur (orphelins filtrés). Ni
     *  `ged_file_ids`, ni `files`, ni `original_name` n'existent. */
    ged_files: { id: string; name: string }[]
    /**
     * Les trois rattachements que le serveur RÉSOUT et que ce type ignorait.
     *
     * 🔴 Ils sont la moitié de la valeur du registre : un échange qui ne dit ni
     * sur quel lot il porte, ni quelle pièce il a fait naître, ni quelle échéance
     * il a posée, n'est qu'une note. Absents du type, ils arrivaient dans la
     * réponse et l'écran ne pouvait pas les lire.
     */
    lot: { id: string; lot_number: string; title: string } | null
    deadline: {
        id: string
        kind: string
        due_at: string
        is_hard: boolean
        done_at: string | null
    } | null
    piece: { id: string; label: string; state: string; assignee_id: string | null } | null
    created_by: { id: string; name: string } | null
    /** Scrubbing RGPD : les faits restent, les zones de texte libre partent. */
    anonymized_at: string | null
    created_at: string
}

// ── TND-017 — veille BOAMP ──────────────────────────────────────────────────

export interface WatchProfile {
    id: string
    entity_id: string
    entity_name: string | null
    /**
     * FACULTATIF depuis la migration 657 : l'écran ne le demande plus, il dérive
     * un libellé des critères eux-mêmes — « bureautique, numérique · Gironde »
     * informe mieux que « Profil 1 ».
     */
    name: string | null
    keywords: string[]
    cpv_codes: string[]
    departements: string[]
    min_estimated_amount: string | null
    max_estimated_amount: string | null
    is_active: boolean
    digest_enabled: boolean
}

/**
 * L'état de la veille — ce que rend `GET /tender-watch/summary`.
 *
 * ⚠️ **Remplace `WatchItem` / `WatchFeed` (migration 660).** Il n'y a plus de
 * flux d'avis : un avis capté est une FICHE au stage `VEILLE`, servie par le
 * listing des appels d'offres comme n'importe quelle autre.
 */
export interface TenderWatchSummary {
    /** Les fiches au seuil du pipeline. Dédupliqué : une fiche, une ligne. */
    to_triage_count: number
    /**
     * Dernier passage de l'ingestion sur le périmètre. `null` tant qu'aucune
     * passe n'a rien rapporté — la licence ouverte v2.0 impose de citer la
     * source ET sa date de mise à jour, donc l'écran l'affiche telle quelle
     * plutôt que d'inventer « à l'instant ».
     */
    last_sync_at: string | null
    /**
     * Dernier passage planifié RÉUSSI pour CE locataire (`tender_watch_cursor`,
     * C238), en ISO ; repli sur `public.scheduler_runs` sans cette ligne. `null`
     * tant qu'aucune passe n'a réussi pour lui — le cas en local.
     *
     * 🔴 Ce n'est PAS `last_sync_at`, et la distinction est tout l'intérêt du
     * champ : `last_sync_at` date les DONNÉES, celui-ci date le PASSAGE. Une
     * nuit sans consultation correspondante laisse le premier inchangé et fait
     * avancer le second.
     *
     * ⚠️ Un clic sur « Rechercher maintenant » ne le fait PAS bouger : une
     * passe manuelle n'écrit ni la ligne du locataire ni le registre. Décision PO
     * du 2026-09-20 — sinon le geste masquerait un tour de nuit mort.
     */
    last_run_at: string | null
    /**
     * Statut du dernier passage planifié pour CE locataire : `ok` ou `error`
     * (avis perdu, ingestion qui lève, source, délai, critères ou plan) ; le
     * repli global peut dire `timeout`. Jamais `running` (passe en cours).
     */
    last_run_status: string | null
    /** Lignes de critères actives. Zéro ⇒ l'ingestion ne peut rien ramener. */
    active_profiles: number
}

/** Erreur RFC 7807 du module — `extra` porte de quoi AGIR, pas de la décoration. */
export interface TenderProblem {
    type?: string
    title?: string
    detail?: string
    code?: string
    [key: string]: unknown
}

/**
 * 🔴 **`detail` n'est PRESQUE JAMAIS une chaîne — et le cast le prétendait.** Trois
 * formes circulent, et le code n'en lisait qu'une :
 *
 * | Émetteur | Forme de `detail` |
 * |---|---|
 * | `rfc7807_error` (tous nos refus métier) | **objet** `{code, message, …}` |
 * | Validation FastAPI (422) | **tableau** `[{loc, msg, type}, …]` |
 * | `HTTPException(detail="…")` nu | chaîne |
 *
 * `(payload.detail as string)` sur un objet rend l'objet lui-même, et `new Error(objet)`
 * produit un message qui vaut littéralement `"[object Object]"`. **Tout refus du module
 * s'affichait donc ainsi** — la garde de dépôt, le motif d'issue obligatoire, une pièce
 * non modifiable : chacune avait une phrase soignée côté serveur, et aucune n'arrivait à
 * l'écran. Le cast ne convertissait rien, il faisait taire le compilateur.
 */
function phraseDeRefus(payload: TenderProblem): string {
    const detail: unknown = payload.detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (Array.isArray(detail)) {
        // Validation FastAPI : on rend les messages, pas les chemins de champs —
        // « body.state : valeur inattendue » ne dit rien à un utilisateur.
        const messages = detail
            .map((d) => (d && typeof d === 'object' ? (d as { msg?: string }).msg : null))
            .filter((m): m is string => Boolean(m))
        if (messages.length) return messages.join(' · ')
    }
    if (detail && typeof detail === 'object') {
        const m = (detail as { message?: unknown }).message
        if (typeof m === 'string' && m.trim()) return m
    }
    const titre = payload.title
    return typeof titre === 'string' ? titre : ''
}

export async function handleTenderError(res: Response): Promise<never> {
    let payload: TenderProblem = {}
    try {
        payload = (await res.json()) as TenderProblem
    } catch {
        /* réponse sans corps : on garde le statut, seule information disponible */
    }
    // Refus sans donnée propre (410 `TENDER_ANONYMIZED`) : la phrase d'écran, par son CODE.
    const error = new Error(
        phraseDEcranDuRefus(payload) ?? (phraseDeRefus(payload) || `HTTP ${res.status}`),
    ) as Error & { problem?: TenderProblem; status?: number }
    error.problem = payload
    error.status = res.status
    throw error
}

/**
 * 🔴 **Les 17 stages du moteur, dans l'ordre du déroulé.**
 *
 * Vocabulaire FERMÉ : contrairement aux statuts — que l'organisme renomme
 * librement — un stage est une clé sur laquelle raisonnent les gardes, les KPI
 * et le kanban. Il a donc une clé i18n, et cette liste est la seule autorité
 * front sur son contenu.
 *
 * ⚠️ **Elle a été écrite à la main une fois, et il en manquait SIX.**
 * `TendersListPage` composait ses options de filtre à partir d'une liste de
 * onze codes recopiés de mémoire. Or une colonne `type: 'enum'` dont la valeur
 * reçue n'est pas dans `enumOptions` **retombe sur la valeur brute** : une fiche
 * au stage absent affichait « INFRUCTUEUX » en capitales au milieu de
 * « Perdu », « Veille », « Déposé ». Le défaut n'était visible que sur ce
 * stage-là parce que le jeu de démonstration est le seul à en produire un —
 * `DCE_RETIRE`, `GO_NOGO`, `QUESTIONS`, `OUVERTURE` et
 * `PIECES_COMPLEMENTAIRES` portaient exactement le même défaut, en sommeil.
 *
 * La liste ne se recopie plus : `stagesDuMoteur.test.ts` la dérive de
 * `backend/src/domain/sales/tenders.py` et rougit si les deux divergent.
 */
export const TENDER_STAGES = [
    'VEILLE',
    'DCE_RETIRE',
    'GO_NOGO',
    'QUESTIONS',
    'REDACTION',
    'DEPOSE',
    'OUVERTURE',
    'ANALYSE',
    'PIECES_COMPLEMENTAIRES',
    'ATTRIBUE_SOUS_RESERVE',
    'ATTRIBUE',
    'NOTIFIE',
    'EXECUTION',
    'PERDU',
    'SANS_SUITE',
    'INFRUCTUEUX',
    'ABANDONNE',
] as const

export type TenderStageCode = (typeof TENDER_STAGES)[number]
